import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const FAMOUS_PANGRAMS = [
  { text: 'The quick brown fox jumps over the lazy dog',        note: "World's most famous pangram",         chars: 43 },
  { text: 'Pack my box with five dozen liquor jugs',            note: 'Compact classic',                     chars: 38 },
  { text: 'Sphinx of black quartz, judge my vow',               note: "Typographer's favourite",             chars: 36 },
  { text: 'How vexingly quick daft zebras jump',                note: 'Vivid and concise',                   chars: 35 },
  { text: 'The five boxing wizards jump quickly',               note: 'Sports-themed',                       chars: 36 },
  { text: 'Jackdaws love my big sphinx of quartz',              note: 'Atmospheric and popular',             chars: 37 },
  { text: 'Blowzy red vixens fight for a quick jump',           note: 'Nature-themed',                       chars: 40 },
  { text: 'Vexed nymphs go for quick waltz job',                note: 'Dance-themed',                        chars: 35 },
  { text: 'Glib jocks quiz nymph to vex dwarf',                 note: 'Fantasy-themed',                      chars: 34 },
  { text: 'Waltz, nymph, for quick jigs vex bud',               note: 'Musical pangram',                     chars: 36 },
  { text: 'Quick wafting zephyrs vex bold Jim',                  note: 'Weather-themed',                      chars: 34 },
  { text: 'Five quacking zephyrs jolt my wax bed',              note: 'Whimsical and short',                 chars: 37 },
  { text: 'Jived fox nymph grabs quick waltz',                  note: 'One of the shorter pangrams (33)',    chars: 33 },
  { text: 'Cwm fjord bank glyphs vext quiz',                    note: 'Among the shortest known (31 chars)', chars: 31 },
  { text: 'Mr. Jock, TV quiz PhD, bags few lynx',               note: 'Modern and quirky',                   chars: 36 },
  { text: 'Amazingly few discotheques provide jukeboxes',       note: 'Music-themed',                        chars: 44 },
  { text: 'The jay, pig, fox, zebra, and my wolves quack',      note: 'Animal kingdom',                      chars: 46 },
  { text: 'Six big devils from Japan quickly forgot how to waltz', note: 'Adventure-themed',                chars: 52 },
  { text: 'Bright vixens jump; dozy fowl quack',                note: 'Punchy imagery',                      chars: 35 },
  { text: 'Sympathizing would fix Quaker objectives',           note: 'Unusual vocabulary',                  chars: 40 },
];

function analyze(text) {
  const used = new Set(text.toUpperCase().replace(/[^A-Z]/g, '').split('').filter(Boolean));
  const missing = ALPHABET.filter(l => !used.has(l));
  return { used, missing, isPangram: missing.length === 0 };
}

export default function PangramChecker() {
  const [text, setText]   = useState('');
  const [copied, setCopied] = useState(null);
  const textareaRef = useRef(null);

  const { used, missing, isPangram } = useMemo(() => analyze(text), [text]);
  const letterCount  = text.replace(/[^a-zA-Z]/g, '').length;
  const totalChars   = text.length;

  function loadPangram(t) {
    setText(t);
    textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    textareaRef.current?.focus();
  }

  function randomPangram() {
    const p = FAMOUS_PANGRAMS[Math.floor(Math.random() * FAMOUS_PANGRAMS.length)];
    loadPangram(p.text);
  }

  function copyText(t) {
    navigator.clipboard.writeText(t).then(() => {
      setCopied(t); setTimeout(() => setCopied(null), 1500);
    });
  }

  const completionPct = Math.round((used.size / 26) * 100);

  return (
    <div className="game-page">
      <PageSEO page="pangram-checker" />
      <div className="container" style={{ maxWidth: 720 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔤</span>
          <h1>Pangram Checker</h1>
          <p>Check if your text uses every letter of the alphabet — then try writing your own!</p>
        </div>

        {/* ── Input area ── */}
        <div style={{ background: '#f8fafc', border: '2px solid var(--border)', borderRadius: 16, padding: '1.25rem', marginBottom: '1.25rem' }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder='Type or paste any text here… e.g. "The quick brown fox jumps over the lazy dog"'
            rows={4}
            style={{ width: '100%', border: '2px solid var(--border)', borderRadius: 12, padding: '0.75rem 1rem', fontFamily: 'Nunito, sans-serif', fontSize: '1rem', resize: 'vertical', outline: 'none', background: '#fff', color: '#1e293b', lineHeight: 1.6 }}
          />

          {/* Stats row */}
          <div className="d-flex gap-3 flex-wrap mt-2 mb-3" style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 700 }}>
            <span>{totalChars} chars</span>
            <span>{letterCount} letters</span>
            <span>{used.size} / 26 unique</span>
            {text && <span style={{ color: isPangram ? '#059669' : '#d97706' }}>{completionPct}% complete</span>}
          </div>

          {/* Action buttons */}
          <div className="d-flex gap-2 flex-wrap">
            <button onClick={randomPangram} className="solve-btn" style={{ fontSize: '0.9rem', padding: '0.5rem 1.2rem' }}>
              🎲 Random Pangram
            </button>
            {text && (
              <>
                <button onClick={() => copyText(text)}
                  style={{ borderRadius: 50, border: '2px solid var(--border)', background: copied === text ? '#dcfce7' : '#fff', color: copied === text ? '#15803d' : '#374151', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
                  {copied === text ? '✅ Copied!' : '📋 Copy Text'}
                </button>
                <button onClick={() => setText('')}
                  style={{ borderRadius: 50, border: '2px solid var(--border)', background: '#fff', color: '#64748b', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
                  🗑 Clear
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Result badge ── */}
        {text && (
          <div style={{
            textAlign: 'center', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1.25rem',
            background: isPangram ? '#dcfce7' : '#fef3c7',
            border: `2px solid ${isPangram ? '#86efac' : '#fde68a'}`,
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>
              {isPangram ? '🎉' : '🔍'}
            </div>
            <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.2rem', color: isPangram ? '#065f46' : '#92400e', marginBottom: '0.25rem' }}>
              {isPangram ? 'Perfect Pangram! All 26 letters used.' : `Not a pangram yet — missing ${missing.length} letter${missing.length !== 1 ? 's' : ''}`}
            </div>
            {!isPangram && missing.length > 0 && (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {missing.map(l => (
                  <span key={l} style={{ background: '#fff', border: '2px solid #fde68a', borderRadius: 8, padding: '2px 10px', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#b45309' }}>
                    {l}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── A–Z letter grid ── */}
        <div style={{ marginBottom: '1.75rem' }}>
          <div className="fw-bold mb-2" style={{ fontSize: '0.85rem', color: '#374151' }}>
            A–Z Coverage {text ? `· ${used.size}/26 letters used` : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ALPHABET.map(letter => {
              const isUsed = used.has(letter);
              return (
                <div key={letter} style={{
                  width: 'clamp(28px, 7vw, 38px)', height: 'clamp(28px, 7vw, 38px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 8, fontFamily: 'Nunito, sans-serif', fontWeight: 800,
                  fontSize: 'clamp(0.7rem, 2.5vw, 0.95rem)',
                  background: isUsed ? '#538d4e' : '#f1f5f9',
                  color: isUsed ? '#fff' : '#94a3b8',
                  border: `2px solid ${isUsed ? '#538d4e' : '#e2e8f0'}`,
                  transition: 'background 0.25s, color 0.25s, border-color 0.25s',
                  userSelect: 'none',
                }}>
                  {letter}
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          {text && (
            <div style={{ marginTop: 10 }}>
              <div style={{ background: '#e2e8f0', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                <div style={{
                  width: `${completionPct}%`, height: '100%', borderRadius: 99,
                  background: isPangram ? '#059669' : '#f59e0b',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}
        </div>

        {/* ── How to write a pangram ── */}
        <div style={{ background: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1.75rem' }}>
          <div className="fw-bold mb-1" style={{ color: '#1e40af', fontSize: '0.9rem' }}>💡 Write Your Own Pangram</div>
          <p style={{ fontSize: '0.85rem', color: '#1e40af', margin: 0, lineHeight: 1.6 }}>
            A pangram uses all 26 letters at least once. Watch the green tiles fill up as you type.
            The hardest letters to include are <strong>J, Q, X, Z</strong> — try words like
            "jinx", "quiz", "fox", or "jazz" to tick them off quickly.
            The shortest known English pangram is just <strong>26 letters</strong>!
          </p>
        </div>

        {/* ── Famous Pangrams Gallery ── */}
        <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.3rem', color: '#1e293b', marginBottom: '0.35rem' }}>
          Famous Pangrams
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1rem' }}>
          {FAMOUS_PANGRAMS.length} classic pangrams — click <strong>Try it</strong> to load into the checker above
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: '1.5rem' }}>
          {FAMOUS_PANGRAMS.map(({ text: pt, note, chars }) => (
            <div key={pt} style={{
              background: '#fff', border: '2px solid var(--border)', borderRadius: 14,
              padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              {/* Pangram text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: 2 }}>
                  {pt}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{note}</span>
                  <span style={{
                    background: chars <= 34 ? '#dcfce7' : chars <= 40 ? '#fef3c7' : '#f1f5f9',
                    color:      chars <= 34 ? '#065f46' : chars <= 40 ? '#92400e' : '#475569',
                    fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px', borderRadius: 99,
                  }}>
                    {chars} chars {chars <= 34 ? '🏆' : ''}
                  </span>
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                <button onClick={() => copyText(pt)}
                  style={{ borderRadius: 99, border: '1.5px solid var(--border)', background: copied === pt ? '#dcfce7' : '#f8fafc', color: copied === pt ? '#15803d' : '#374151', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.75rem', padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {copied === pt ? '✅' : '📋'}
                </button>
                <button onClick={() => loadPangram(pt)}
                  style={{ borderRadius: 99, border: 'none', background: 'var(--primary)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.75rem', padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  ▶ Try it
                </button>
              </div>
            </div>
          ))}
        </div>

        <SeoContent game="pangram-checker" />
      </div>
    </div>
  );
}
